package com.presentocracy.shared.network

import com.presentocracy.shared.contract.AddContributionRequest
import com.presentocracy.shared.contract.CreateGiftRequest
import com.presentocracy.shared.contract.CreateShareLinkRequest
import com.presentocracy.shared.contract.CreateWishlistRequest
import com.presentocracy.shared.contract.ContactDiscoveryRequest
import com.presentocracy.shared.contract.DiscoveryResponse
import com.presentocracy.shared.contract.EnableGroupGiftRequest
import com.presentocracy.shared.contract.ErrorEnvelope
import com.presentocracy.shared.contract.FriendRequest
import com.presentocracy.shared.contract.FriendResponse
import com.presentocracy.shared.contract.FriendProfileResponse
import com.presentocracy.shared.contract.GiftResponse
import com.presentocracy.shared.contract.GoogleAuthRequest
import com.presentocracy.shared.contract.GroupGiftResponse
import com.presentocracy.shared.contract.LoginRequest
import com.presentocracy.shared.contract.LogoutRequest
import com.presentocracy.shared.contract.MessageResponse
import com.presentocracy.shared.contract.ParseProductUrlRequest
import com.presentocracy.shared.contract.PasswordChangeRequest
import com.presentocracy.shared.contract.PasswordResetConfirmRequest
import com.presentocracy.shared.contract.PasswordResetRequest
import com.presentocracy.shared.contract.ProductMetadataResponse
import com.presentocracy.shared.contract.RefreshRequest
import com.presentocracy.shared.contract.RegisterRequest
import com.presentocracy.shared.contract.RegisterResponse
import com.presentocracy.shared.contract.ReminderSettingsRequest
import com.presentocracy.shared.contract.ReminderSettingsResponse
import com.presentocracy.shared.contract.ReservationResponse
import com.presentocracy.shared.contract.ReservationStatusRequest
import com.presentocracy.shared.contract.SessionResponse
import com.presentocracy.shared.contract.ShareLinkResponse
import com.presentocracy.shared.contract.UpcomingBirthdaysResponse
import com.presentocracy.shared.model.DEFAULT_UPCOMING_BIRTHDAY_DAYS
import com.presentocracy.shared.contract.UpdateContributionRequest
import com.presentocracy.shared.contract.UpdateProfileRequest
import com.presentocracy.shared.contract.UpdateDiscoverySettingsRequest
import com.presentocracy.shared.contract.UpdateWishlistRequest
import com.presentocracy.shared.contract.ResendVerificationRequest
import com.presentocracy.shared.contract.VerifyEmailCodeRequest
import com.presentocracy.shared.contract.VerifyEmailRequest
import com.presentocracy.shared.contract.WishlistAclRequest
import com.presentocracy.shared.model.Friendship
import com.presentocracy.shared.model.FriendshipStatus
import com.presentocracy.shared.model.ReservationStatus
import com.presentocracy.shared.model.UserProfile
import com.presentocracy.shared.model.Wishlist
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.accept
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.Headers
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.http.encodeURLParameter
import io.ktor.client.request.forms.MultiPartFormDataContent
import io.ktor.client.request.forms.formData
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json
import kotlinx.serialization.ExperimentalSerializationApi

class ApiClientException(
    val status: HttpStatusCode,
    val error: ErrorEnvelope,
) : RuntimeException("${error.code}: ${error.message}")

class ProfileUpdateNotAppliedException : IllegalStateException("The server did not save the submitted profile changes")

@OptIn(ExperimentalSerializationApi::class)
class PresentocracyApiClient(
    private val baseUrl: String,
    private val client: HttpClient = HttpClient {
        install(ContentNegotiation) {
            json(Json { ignoreUnknownKeys = true; explicitNulls = false })
        }
    },
) : AutoCloseable {
    private var accessToken: String? = null

    fun setAccessToken(token: String?) {
        accessToken = token
    }

    suspend fun register(request: RegisterRequest): RegisterResponse =
        client.post(url("/api/v1/auth/register")) { jsonBody(request) }.checkedBody()

    suspend fun verifyEmail(token: String): MessageResponse =
        client.post(url("/api/v1/auth/verify-email")) { jsonBody(VerifyEmailRequest(token)) }
            .checkedBody()

    suspend fun verifyEmailCode(email: String, code: String): MessageResponse =
        client.post(url("/api/v1/auth/verify-email-code")) {
            jsonBody(VerifyEmailCodeRequest(email, code))
        }.checkedBody()

    suspend fun resendVerification(email: String): MessageResponse =
        client.post(url("/api/v1/auth/resend-verification")) {
            jsonBody(ResendVerificationRequest(email))
        }.checkedBody()

    suspend fun login(request: LoginRequest): SessionResponse =
        client.post(url("/api/v1/auth/login")) { jsonBody(request) }
            .checkedBody<SessionResponse>()
            .also { accessToken = it.accessToken }

    suspend fun loginWithGoogle(idToken: String): SessionResponse =
        client.post(url("/api/v1/auth/google")) { jsonBody(GoogleAuthRequest(idToken)) }
            .checkedBody<SessionResponse>()
            .also { accessToken = it.accessToken }

    suspend fun refresh(refreshToken: String): SessionResponse =
        client.post(url("/api/v1/auth/refresh")) { jsonBody(RefreshRequest(refreshToken)) }
            .checkedBody<SessionResponse>()
            .also { accessToken = it.accessToken }

    suspend fun logout(refreshToken: String): MessageResponse =
        client.post(url("/api/v1/auth/logout")) { jsonBody(LogoutRequest(refreshToken)) }
            .checkedBody<MessageResponse>()
            .also { accessToken = null }

    suspend fun requestPasswordReset(email: String): MessageResponse =
        client.post(url("/api/v1/auth/password-reset")) { jsonBody(PasswordResetRequest(email)) }
            .checkedBody()

    suspend fun requestEmailChange(newEmail: String, currentPassword: String): com.presentocracy.shared.contract.EmailChangeChallenge =
        client.post(url("/api/v1/me/email/change")) { jsonBody(com.presentocracy.shared.contract.EmailChangeRequest(newEmail, currentPassword)); authHeader() }.checkedBody()

    suspend fun confirmEmailChange(challengeId: String, code: String): UserProfile =
        client.post(url("/api/v1/me/email/confirm")) { jsonBody(com.presentocracy.shared.contract.EmailChangeConfirmRequest(challengeId, code)); authHeader() }.checkedBody()

    suspend fun confirmPasswordReset(token: String, newPassword: String): MessageResponse =
        client.post(url("/api/v1/auth/password-reset/confirm")) {
            jsonBody(PasswordResetConfirmRequest(token, newPassword))
        }.checkedBody()

    suspend fun currentUser(): UserProfile =
        client.getWithAuth(url("/api/v1/me")).checkedBody()

    suspend fun profilePreview(): com.presentocracy.shared.model.ProfilePreview =
        client.getWithAuth(url("/api/v1/me/profile/preview")).checkedBody()

    suspend fun deleteAccount(): MessageResponse =
        client.delete(url("/api/v1/me")) { authHeader() }.checkedBody()

    suspend fun wishlistFromNavigation(id: String): Wishlist {
        require(com.presentocracy.shared.contract.parseWishlistNavigationId(id) != null) { "Invalid wishlist identifier" }
        return client.get(url("/api/v1/wishlist-links/$id"))
            .checkedBody<com.presentocracy.shared.contract.WishlistResponse>().wishlist
    }

    suspend fun wishlists(): List<Wishlist> =
        client.getWithAuth(url("/api/v1/wishlists")).checkedBody()

    suspend fun wishlistsForUser(userId: String): List<Wishlist> =
        client.getWithAuth(url("/api/v1/users/$userId/wishlists")).checkedBody()

    suspend fun friendProfile(userId: String): FriendProfileResponse =
        client.getWithAuth(url("/api/v1/users/$userId/profile")).checkedBody()

    suspend fun createWishlist(request: CreateWishlistRequest): Wishlist =
        client.post(url("/api/v1/wishlists")) { jsonBody(request); authHeader() }
            .checkedBody<com.presentocracy.shared.contract.WishlistResponse>().wishlist

    suspend fun updateWishlist(wishlistId: String, request: UpdateWishlistRequest): Wishlist =
        client.put(url("/api/v1/wishlists/$wishlistId")) { jsonBody(request); authHeader() }
            .checkedBody<com.presentocracy.shared.contract.WishlistResponse>().wishlist

    suspend fun deleteWishlist(wishlistId: String): MessageResponse =
        client.delete(url("/api/v1/wishlists/$wishlistId")) { authHeader() }.checkedBody()

    suspend fun createShareLink(wishlistId: String): com.presentocracy.shared.contract.ShareLinkResponse =
        client.post(url("/api/v1/wishlists/$wishlistId/share-links")) {
            jsonBody(CreateShareLinkRequest()); authHeader()
        }.checkedBody()

    suspend fun regenerateShareLink(wishlistId: String): ShareLinkResponse =
        client.put(url("/api/v1/wishlists/$wishlistId/share-links")) {
            jsonBody(CreateShareLinkRequest()); authHeader()
        }.checkedBody()

    suspend fun revokeShareLinks(wishlistId: String): MessageResponse =
        client.delete(url("/api/v1/wishlists/$wishlistId/share-links")) { authHeader() }
            .checkedBody()

    suspend fun setWishlistAcl(wishlistId: String, request: WishlistAclRequest): MessageResponse =
        client.post(url("/api/v1/wishlists/$wishlistId/acl")) { jsonBody(request); authHeader() }
            .checkedBody()

    suspend fun removeWishlistAcl(wishlistId: String, userId: String): MessageResponse =
        client.delete(url("/api/v1/wishlists/$wishlistId/acl/$userId")) { authHeader() }
            .checkedBody()

    suspend fun createGift(
        wishlistId: String,
        request: CreateGiftRequest
    ): com.presentocracy.shared.model.Gift =
        client.post(url("/api/v1/wishlists/$wishlistId/gifts")) { jsonBody(request); authHeader() }
            .checkedBody<GiftResponse>().gift

    suspend fun updateGift(
        giftId: String,
        request: com.presentocracy.shared.contract.UpdateGiftRequest
    ): com.presentocracy.shared.model.Gift =
        client.put(url("/api/v1/gifts/$giftId")) { jsonBody(request); authHeader() }
            .checkedBody<GiftResponse>().gift

    suspend fun deleteGift(giftId: String): MessageResponse =
        client.delete(url("/api/v1/gifts/$giftId")) { authHeader() }.checkedBody()

    suspend fun uploadImage(
        bytes: ByteArray,
        fileName: String = "image.jpg",
        contentType: ContentType = ContentType.Image.JPEG,
    ): com.presentocracy.shared.contract.ImageUploadResponse =
        client.post(url("/api/v1/images")) {
            authHeader()
            setBody(
                MultiPartFormDataContent(
                    formData {
                        append(
                            "file",
                            bytes,
                            Headers.build {
                                append(HttpHeaders.ContentType, contentType.toString())
                                append(HttpHeaders.ContentDisposition, "filename=\"$fileName\"")
                            },
                        )
                    },
                ),
            )
        }.checkedBody()

    suspend fun attachGiftImage(giftId: String, imageId: String): MessageResponse =
        client.post(url("/api/v1/gifts/$giftId/image?imageId=${imageId.encodeURLParameter()}")) {
            authHeader()
        }.checkedBody()

    suspend fun reservation(giftId: String): ReservationResponse =
        client.getWithAuth(url("/api/v1/gifts/$giftId/reservation")).checkedBody()

    suspend fun reserveGift(giftId: String): ReservationResponse =
        client.post(url("/api/v1/gifts/$giftId/reservation")) { authHeader() }.checkedBody()

    suspend fun cancelGiftReservation(giftId: String): MessageResponse =
        client.delete(url("/api/v1/gifts/$giftId/reservation")) { authHeader() }.checkedBody()

    suspend fun updateReservationStatus(
        giftId: String,
        status: ReservationStatus
    ): ReservationResponse =
        client.put(url("/api/v1/gifts/$giftId/reservation/status")) {
            jsonBody(ReservationStatusRequest(status)); authHeader()
        }.checkedBody()

    suspend fun groupGift(giftId: String): GroupGiftResponse =
        client.getWithAuth(url("/api/v1/gifts/$giftId/group")).checkedBody()

    suspend fun enableGroupGift(
        giftId: String,
        request: EnableGroupGiftRequest
    ): GroupGiftResponse =
        client.post(url("/api/v1/gifts/$giftId/group")) { jsonBody(request); authHeader() }
            .checkedBody()

    suspend fun disableGroupGift(giftId: String): MessageResponse =
        client.delete(url("/api/v1/gifts/$giftId/group")) { authHeader() }.checkedBody()

    suspend fun addContribution(
        giftId: String,
        request: AddContributionRequest
    ): GroupGiftResponse =
        client.post(url("/api/v1/gifts/$giftId/group/contributions")) { jsonBody(request); authHeader() }
            .checkedBody()

    suspend fun updateContribution(
        giftId: String,
        contributionId: String,
        request: UpdateContributionRequest
    ): GroupGiftResponse =
        client.put(url("/api/v1/gifts/$giftId/group/contributions/$contributionId")) {
            jsonBody(request); authHeader()
        }.checkedBody()

    suspend fun removeContribution(giftId: String, contributionId: String): MessageResponse =
        client.delete(url("/api/v1/gifts/$giftId/group/contributions/$contributionId")) { authHeader() }
            .checkedBody()

    suspend fun parseProductMetadata(productUrl: String): ProductMetadataResponse =
        client.post(url("/api/v1/product-metadata")) {
            jsonBody(ParseProductUrlRequest(productUrl)); authHeader()
        }.checkedBody()

    suspend fun upcomingBirthdays(days: Int = DEFAULT_UPCOMING_BIRTHDAY_DAYS): UpcomingBirthdaysResponse =
        client.getWithAuth(url("/api/v1/birthdays/upcoming?days=$days")).checkedBody()

    suspend fun searchUsers(query: String): List<com.presentocracy.shared.model.UserCard> =
        client.getWithAuth(url("/api/v1/social/search?q=${query.trim().encodeURLParameter()}"))
            .checkedBody<DiscoveryResponse>().users

    suspend fun searchUsersPage(query: String, cursor: String? = null): com.presentocracy.shared.contract.UserSearchPage =
        client.getWithAuth(url("/api/v1/social/search/page?q=${query.trim().encodeURLParameter()}" +
            (cursor?.let { "&cursor=${it.encodeURLParameter()}" } ?: ""))).checkedBody()

    suspend fun discoverContacts(contactHashes: List<String>): List<com.presentocracy.shared.model.UserCard> =
        client.post(url("/api/v1/social/contacts/discover")) {
            jsonBody(ContactDiscoveryRequest(contactHashes)); authHeader()
        }.checkedBody<DiscoveryResponse>().users

    suspend fun discoverContactMatches(contactHashes: List<String>): DiscoveryResponse =
        client.post(url("/api/v1/social/contacts/discover")) {
            jsonBody(ContactDiscoveryRequest(contactHashes)); authHeader()
        }.checkedBody()

    suspend fun privacySettings(): com.presentocracy.shared.contract.PrivacySettings =
        client.getWithAuth(url("/api/v1/me/privacy")).checkedBody()

    suspend fun updatePrivacySettings(settings: com.presentocracy.shared.contract.PrivacySettings): com.presentocracy.shared.contract.PrivacySettings =
        client.put(url("/api/v1/me/privacy")) {
            jsonBody(settings); authHeader()
        }.checkedBody()

    suspend fun updateDiscoverySettings(searchable: Boolean, contactDiscoveryEnabled: Boolean): MessageResponse =
        client.put(url("/api/v1/social/settings")) {
            jsonBody(UpdateDiscoverySettingsRequest(searchable, contactDiscoveryEnabled)); authHeader()
        }.checkedBody()

    suspend fun friendships(): List<Friendship> =
        client.getWithAuth(url("/api/v1/social/friends")).checkedBody()

    suspend fun sendFriendRequest(userId: String): Friendship =
        client.post(url("/api/v1/social/friends")) {
            jsonBody(FriendRequest(userId)); authHeader()
        }.checkedBody<FriendResponse>().friendship

    suspend fun respondToFriendRequest(friendshipId: String, status: FriendshipStatus): Friendship =
        client.post(url("/api/v1/social/friends/$friendshipId/${status.name}")) { authHeader() }
            .checkedBody<FriendResponse>().friendship

    suspend fun updateProfile(request: UpdateProfileRequest): UserProfile {
        val updated = client.put(url("/api/v1/me/profile")) {
            jsonBody(request); authHeader()
        }.checkedBody<UserProfile>()
        // Null means "leave unchanged"; an empty string explicitly clears the bio.
        request.bio?.let { submitted ->
            if (submitted.trim().ifEmpty { null } != updated.bio?.trim()?.ifEmpty { null }) {
                throw ProfileUpdateNotAppliedException()
            }
        }
        return updated
    }

    suspend fun changePassword(currentPassword: String, newPassword: String): MessageResponse =
        client.post(url("/api/v1/me/password")) {
            jsonBody(PasswordChangeRequest(currentPassword, newPassword)); authHeader()
        }.checkedBody()

    suspend fun accountSecurity(): com.presentocracy.shared.contract.AccountSecurity =
        client.getWithAuth(url("/api/v1/me/security")).checkedBody()

    suspend fun revokeSession(sessionId: String): MessageResponse =
        client.delete(url("/api/v1/me/sessions/${sessionId.encodeURLParameter()}")) { authHeader() }.checkedBody()

    suspend fun revokeOtherSessions(): MessageResponse =
        client.post(url("/api/v1/me/sessions/revoke-others")) { authHeader() }.checkedBody()

    suspend fun reminderSettings(): ReminderSettingsResponse =
        client.getWithAuth(url("/api/v1/birthdays/settings")).checkedBody()

    suspend fun updateReminderSettings(enabled: Boolean, offsetsDays: List<Int> = listOf(7, 1)): ReminderSettingsResponse =
        client.put(url("/api/v1/birthdays/settings")) {
            jsonBody(ReminderSettingsRequest(enabled, offsetsDays)); authHeader()
        }.checkedBody()

    override fun close() = client.close()

    private fun url(path: String): String = baseUrl.trimEnd('/') + path

    private inline fun <reified T> io.ktor.client.request.HttpRequestBuilder.jsonBody(value: T) {
        contentType(ContentType.Application.Json)
        accept(ContentType.Application.Json)
        setBody(value)
    }

    private fun io.ktor.client.request.HttpRequestBuilder.authHeader() {
        accessToken?.let { header(HttpHeaders.Authorization, "Bearer $it") }
    }

    private suspend fun io.ktor.client.HttpClient.getWithAuth(path: String) =
        get(path) { authHeader() }

    private suspend inline fun <reified T> io.ktor.client.statement.HttpResponse.checkedBody(): T {
        if (status != HttpStatusCode.OK && status != HttpStatusCode.Created) {
            throw ApiClientException(status, runCatching { body<ErrorEnvelope>() }
                .getOrElse { ErrorEnvelope("HTTP_${status.value}", "The request failed") })
        }
        return body()
    }
}
